import { SelectProps } from "antd";
import { AdvertRegulationInput, EnrichmentCompletedPayload, TaxAdvert } from "../advert/types";
import { TaxFlow } from "../../../../contexts/taxFlowStore";
import { calculateEquipmentPrice } from "../newPrice/utils/calcNewPrice";
import { EquipmentCatalog, NewPriceManager } from "../../../../types/newPriceTypes";
import { applyPackagesToEquipment } from "../newPrice/utils/mappers";
import { calculateTradePrice, calculateTradePriceWithEquipment } from "@dmf/calculator-library";
import calculateAdvertRegulation from "../advert/calculateAdvertRegulation";
import { renderErrorMessage } from "../../../../components/messages/errorMessage";
import { UpsertTaxScenarioPayload } from "../../../../services/taxScenarioService";
import moment from 'moment'

export const internalEnrichmentFinishEvent = "internal-enrichment-finish"
export function effectivePrice(autoCalc: boolean, calcPrice: number | null, manualPrice: number | null) {
  return autoCalc ? calcPrice : manualPrice;
}

export const exclusionReasons: SelectProps<number>['options'] = [
  // { value: 1, label: "Indgår" },
  { value: 2, label: "Billigste" },
  { value: 3, label: "Dyreste" },
  { value: 4, label: "Afvigende modelgeneration" },
  { value: 5, label: "Fejl i annonce" },
  { value: 6, label: "Ej detailsalg" },
  { value: 7, label: "Forældet prissætning" },
  { value: 8, label: "Annonce dublet" },
  { value: 9, label: "Afvigende kmstand" },
  { value: 10, label: "Afvigende udstyrsvariant" },
  { value: 11, label: "Afvigende årgang" },
  { value: 12, label: "Afvigende gearkasse" },
  { value: 13, label: "Facelift" },
  { value: 14, label: "Mangel i data" },
  { value: 15, label: "For mange liggedage" },
  { value: 16, label: "Afvigende karosseri" },
  { value: 17, label: "Anmelders annonce" },
  { value: 18, label: "Afvigende motorydelse" },
  { value: 19, label: "Afvigende ekstraudstyr" },
]


function determineMountedEquipment(payload: EnrichmentCompletedPayload | null): string[] {
  if (!payload?.mountedEquipment?.length && !payload?.author) {
    return payload?.vinData?.equipment ?? []
  }
  return payload?.mountedEquipment ?? []
}

export function initializeAdvertEquipment(taxFlow: TaxFlow, payload: EnrichmentCompletedPayload | null): NewPriceManager {
  const newPriceManager = { ...taxFlow.newPrice?.manager }
  const mountedEquipment = new Set(determineMountedEquipment(payload));
  const vinEquipment = new Set(payload?.vinData?.equipment)
  let equipment = newPriceManager?.data?.equipment
    ?.map(eq => {
      return {
        ...eq,
        mounted: eq.code ? mountedEquipment.has(eq.code) : false,
        isVinEquipment: eq.code ? vinEquipment.has(eq.code) : false,
      }
    }) ?? []

  equipment = applyPackagesToEquipment(equipment)

  return {
    ...newPriceManager,
    data: {
      ...newPriceManager.data,
      newPrices: newPriceManager.data?.newPrices ?? null,
      selectedNewPrice: newPriceManager.data?.selectedNewPrice ?? null,
      equipment: equipment ?? [],
    },
    values: {
      ...newPriceManager.values,
      newPrice: newPriceManager.values?.newPrice ?? null,
      equipmentPrice: calculateEquipmentPrice(equipment ?? [])
    }

  }
}

export function applyEquipmentChangesToAdvert(taxFlow: TaxFlow, advert: TaxAdvert): NewPriceManager {
  // accoutn for custom equipment
  // Merge in custom equipment. check if same equipment is in evaluation, then take that, otherwise keep the advert custom equipment as is
  const newPriceManager = { ...taxFlow?.newPrice?.manager }


  const evalautionCustomEquipment = new Set(newPriceManager?.data?.equipment.filter(eq => eq.isCustom).map(eq => eq.code));
  const advertCustomEquipment = advert?.config?.newPriceManager?.data.equipment.filter(eq => eq.isCustom);
  // Filter for equipment that is both custom (user added) and unique to the advert
  const customToKeep = advertCustomEquipment?.filter(adEq => {
    return !evalautionCustomEquipment.has(adEq.code)
  })

  const mountedEquipment = new Set(advert?.config?.newPriceManager?.data.equipment.filter(eq => eq.mounted).map(eq => eq.code))
  const vinEquipment = new Set(advert?.config?.enrichment?.vinData?.equipment)
  let equipment = [
    ...newPriceManager?.data?.equipment?.map(eq => {
      return {
        ...eq,
        mounted: eq.code ? mountedEquipment.has(eq.code) : false,
        isVinEquipment: eq.code ? vinEquipment.has(eq.code) : false,
      }
    }) ?? [],
    ...customToKeep ?? []
  ]
  equipment = applyPackagesToEquipment(equipment)

  return {
    ...newPriceManager,
    data: {
      ...newPriceManager.data,
      newPrices: newPriceManager.data?.newPrices ?? null,
      selectedNewPrice: newPriceManager.data?.selectedNewPrice ?? null,
      equipment,
    },
    values: {
      ...newPriceManager.values,
      newPrice: newPriceManager.values?.newPrice ?? null,
      equipmentPrice: calculateEquipmentPrice(equipment)
    }
  }
}

const MAX_VEHICLE_AGE = 12;
export function initializeAdvertRegulation({ firstRegDate, mileage, salesPrice, evaluationVehicleFirstRegDate }: {
  firstRegDate: string | null | undefined,
  salesPrice: number | null | undefined,
  evaluationVehicleFirstRegDate: string | null | undefined,
  mileage: number | null | undefined
}) {
  const ageInYearToDate = moment().diff(evaluationVehicleFirstRegDate, 'years', true)
  const regulationInput: AdvertRegulationInput = {
    firstRegDate: firstRegDate,
    salesPrice: salesPrice,
    evaluationVehicleFirstRegDate,
    mileage: mileage,
    overrides: {
      ageRegulation: {
        active: ageInYearToDate < MAX_VEHICLE_AGE,
        mileage: undefined,
        price: undefined,
      },
      additionalRegulations: [],
      plates: {
        active: true,
        price: undefined,
      }
    },
  }

  let regulationOutput = null;
  try {
    regulationOutput = calculateAdvertRegulation(regulationInput)
    regulationInput.overrides!.ageRegulation.mileage = regulationOutput.ageRegulatedMileageDifference;
    regulationInput.overrides!.ageRegulation.price = regulationOutput.ageRegulatedPriceDifference;
    regulationInput.overrides!.plates.price = regulationOutput.plates;
  } catch (err) {
    renderErrorMessage(err)
  }

  return {
    regulationInput,
    regulationOutput
  }
}

export function regulateEquipmentList(equipment: EquipmentCatalog[], ageInYearsToDate: number) {
  const regulatedEquipment = equipment
    .filter(eq => (eq.mounted || eq.isForceMounted) && !!eq.amount && !eq.includedInNewPrice && !eq.isIncludedInMountedPackage)
    .map(eq => {
      const price = singleEquipmentRegulationPrice({ price: eq.amount, typeCategory: eq.category }, ageInYearsToDate)
      return {
        ...eq,
        amount: price
      }
    })
    .filter(eq => !!eq.amount)
  return regulatedEquipment
}



type Category = Parameters<typeof calculateTradePriceWithEquipment>[1][number]['category']
type Quality = Parameters<typeof calculateTradePrice>[0]['quality']

export function mapCateory(categoryId: number | null): Category {
  switch (categoryId) {
    case 2: return "NONE_DEDUCTABLE";
    case 3: return "radio_hifi";
    case 4: return "aluminum_rims";
    case 5: return "tow_hook";
    case 6: return "mobile_equipment";
    case 7: return "safety_equipment";
    case 8: return "NONE_DEDUCTABLE";
    default: return "TOTAL_AMOUNT"
  }
}
export function mapQuality(qualityId: number): Quality {
  switch (qualityId) {
    case 2: return "ABOVE_AVERAGE";
    case 4: return "BELOW_AVERAGE";
    default: return "AVERAGE";
  }
}


const CONSTANTS = {
  minimunNewPrice: 10000,
  minimumRadioHifiPrice: 30000,
  ageLimitInYearsToDate: 1,
} as const;

export const singleEquipmentRegulationPrice = (singleEquipment: { typeCategory: number | null, price: number | null }, ageInYearsToDate: number): number => {
  const category = mapCateory(singleEquipment.typeCategory ?? null)
  const price = singleEquipment.price ?? 0;
  if (category === "TOTAL_AMOUNT") return price;
  if (ageInYearsToDate < CONSTANTS.ageLimitInYearsToDate) return price;
  if (price < CONSTANTS.minimunNewPrice) return 0;

  switch (category) {
    case "aluminum_rims": return 0;
    case "radio_hifi": return Math.max(price - CONSTANTS.minimumRadioHifiPrice, 0);
    case "tow_hook": return 0;
    case "mobile_equipment": return 0;
    case "safety_equipment": return 0;
    default: return price
  }
}


export function getScenarioUpsertPayload(taxFlow: TaxFlow, optMode: boolean) {
  const payload: UpsertTaxScenarioPayload = {
    isOptimization: optMode,
    calculation: taxFlow,
    caseFee: taxFlow.offer?.caseFee ? Math.round(taxFlow.offer?.caseFee) : null,
    collateralFee: taxFlow.offer?.collateral?.total ? Math.round(taxFlow.offer?.collateral?.total) : null,
    licensePlateFee: taxFlow.offer?.licensePlateFee ? Math.round(taxFlow.offer?.licensePlateFee) : null,
    externalInfo: taxFlow.externalNote,
    internalInfo: taxFlow.internalNote,
    specialCaseFeeDiscount: taxFlow.offer?.specialCaseFeeDiscount,
    newPrice: {
      baseAmount: taxFlow.newPrice?.newPriceWithoutEquipment ? Math.round(taxFlow.newPrice.newPriceWithoutEquipment) : null,
      featuresAmount: taxFlow.newPrice?.equipmentPrice ? Math.round(taxFlow.newPrice.equipmentPrice) : null,
      taxableValue: taxFlow.newPrice?.taxableValue ? Math.round(taxFlow.newPrice.taxableValue) : null,
      total: taxFlow.newPrice?.newPrice ? Math.round(taxFlow.newPrice.newPrice) : null,
    },
    tradePrice: {
      calculated: taxFlow.tradePrice?.summary?.finalTradePrice ? Math.round(taxFlow.tradePrice.summary.finalTradePrice) : null,
      estimated: taxFlow.tradePrice?.tradePrice ? Math.round(taxFlow.tradePrice.tradePrice) : null
    },
    registrationFee: {
      total: taxFlow.registrationFee?.registrationFee ? Math.round(taxFlow.registrationFee?.registrationFee) : null
    },
  }
  return payload
}
































